import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Tooltip,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconCheck,
  IconX,
  IconTrash,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useAbbreviationsAdmin,
  useCreateAbbreviation,
  useUpdateAbbreviation,
  useToggleAbbreviationActive,
  useDeleteAbbreviation,
} from "../../../hooks/admin/useAbbreviations";
import AbbreviationForm, {
  AbbreviationFormValues,
} from "../../../components/admin/abbreviations/AbbreviationForm";
import { Abbreviation } from "../../../services/admin/abbreviations.service";

const ITEMS_PER_PAGE = 20;

function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

export default function AbbreviationsAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const [editingAbbreviation, setEditingAbbreviation] =
    useState<Abbreviation | null>(null);
  const [abbreviationToToggle, setAbbreviationToToggle] =
    useState<Abbreviation | null>(null);
  const [abbreviationToDelete, setAbbreviationToDelete] =
    useState<Abbreviation | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);

  const {
    data: abbreviations = [],
    isLoading,
    refetch,
  } = useAbbreviationsAdmin();
  const createAbbreviation = useCreateAbbreviation();
  const updateAbbreviation = useUpdateAbbreviation();
  const toggleActive = useToggleAbbreviationActive();
  const deleteAbbreviation = useDeleteAbbreviation();

  const filteredAbbreviations = useMemo(() => {
    if (!debouncedSearch) return abbreviations;
    const q = debouncedSearch.toLowerCase();
    return abbreviations.filter(
      (a) =>
        a.shortcode.toLowerCase().includes(q) ||
        stripHtml(a.expansion).toLowerCase().includes(q)
    );
  }, [abbreviations, debouncedSearch]);

  const totalPages = Math.ceil(filteredAbbreviations.length / ITEMS_PER_PAGE);
  const paginatedAbbreviations = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredAbbreviations.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAbbreviations, page]);

  const handleAdd = () => {
    setEditingAbbreviation(null);
    openFormModal();
  };

  const handleEdit = (abbr: Abbreviation) => {
    setEditingAbbreviation(abbr);
    openFormModal();
  };

  const handleSubmit = async (values: AbbreviationFormValues) => {
    if (editingAbbreviation) {
      await updateAbbreviation.mutateAsync({
        id: editingAbbreviation.id,
        ...values,
      });
    } else {
      await createAbbreviation.mutateAsync(values);
    }
    closeFormModal();
  };

  const handleToggleActive = (abbr: Abbreviation) => {
    setAbbreviationToToggle(abbr);
    openConfirmModal();
  };

  const confirmToggleActive = async () => {
    if (abbreviationToToggle) {
      await toggleActive.mutateAsync({
        id: abbreviationToToggle.id,
        isActive: !abbreviationToToggle.isActive,
      });
      closeConfirmModal();
      setAbbreviationToToggle(null);
    }
  };

  const handleDelete = (abbr: Abbreviation) => {
    setAbbreviationToDelete(abbr);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (abbreviationToDelete) {
      await deleteAbbreviation.mutateAsync(abbreviationToDelete.id);
      closeDeleteModal();
      setAbbreviationToDelete(null);
    }
  };

  const formatUserName = (user: { fullName: string } | null) => {
    if (!user) return "—";
    return user.fullName;
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1400px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Abbreviations</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Manage text expansion shortcodes for pathologist review
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          <Paper p="md" withBorder>
            <Group>
              <TextInput
                placeholder="Search by shortcode or expansion text..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                leftSection={<IconSearch size={16} />}
                style={{ flex: 1 }}
              />
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={() => refetch()}
                loading={isLoading}
                leftSection={<IconRefresh size={16} />}
              >
                Refresh
              </Button>
            </Group>
          </Paper>

          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Abbreviations ({filteredAbbreviations.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                      {Math.min(
                        page * ITEMS_PER_PAGE,
                        filteredAbbreviations.length
                      )}{" "}
                      of {filteredAbbreviations.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredAbbreviations.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {search
                    ? "No abbreviations match the current search"
                    : "No abbreviations found. Click 'Add New' to create one."}
                </Text>
              ) : (
                <Table striped highlightOnHover layout="fixed">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 120 }}>Shortcode</Table.Th>
                      <Table.Th>Expansion Preview</Table.Th>
                      <Table.Th style={{ width: 90 }}>Status</Table.Th>
                      <Table.Th style={{ width: 140 }}>Created By</Table.Th>
                      <Table.Th style={{ width: 110 }}>Updated</Table.Th>
                      <Table.Th style={{ width: 120 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedAbbreviations.map((abbr) => (
                      <Table.Tr
                        key={abbr.id}
                        style={{ opacity: abbr.isActive ? 1 : 0.5 }}
                      >
                        <Table.Td>
                          <Text size="sm" fw={600} ff="monospace">
                            {abbr.shortcode}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" lineClamp={1}>
                            {stripHtml(abbr.expansion)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={abbr.isActive ? "green" : "red"}
                            variant="light"
                            size="sm"
                          >
                            {abbr.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {formatUserName(abbr.createdBy)}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {new Date(abbr.updatedAt).toLocaleDateString()}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(abbr)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip
                              label={abbr.isActive ? "Deactivate" : "Activate"}
                            >
                              <ActionIcon
                                variant="subtle"
                                color={abbr.isActive ? "red" : "green"}
                                onClick={() => handleToggleActive(abbr)}
                              >
                                {abbr.isActive ? (
                                  <IconX size={18} />
                                ) : (
                                  <IconCheck size={18} />
                                )}
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(abbr)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={
            editingAbbreviation ? "Edit Abbreviation" : "Add New Abbreviation"
          }
          size="lg"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <AbbreviationForm
              initialValues={editingAbbreviation || undefined}
              onSubmit={handleSubmit}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={
                  createAbbreviation.isPending || updateAbbreviation.isPending
                }
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editingAbbreviation ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={confirmModalOpened}
          onClose={closeConfirmModal}
          title={
            abbreviationToToggle?.isActive
              ? "Deactivate Abbreviation"
              : "Activate Abbreviation"
          }
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to{" "}
              {abbreviationToToggle?.isActive ? "deactivate" : "activate"} the
              abbreviation <strong>{abbreviationToToggle?.shortcode}</strong>?
            </Text>
            {abbreviationToToggle?.isActive && (
              <Text size="sm" c="dimmed">
                Deactivated abbreviations will no longer expand in the editor.
              </Text>
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeConfirmModal}>
                Cancel
              </Button>
              <Button
                color={abbreviationToToggle?.isActive ? "red" : "green"}
                onClick={confirmToggleActive}
                loading={toggleActive.isPending}
              >
                {abbreviationToToggle?.isActive ? "Deactivate" : "Activate"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Abbreviation"
          size="sm"
          centered
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to permanently delete the abbreviation{" "}
              <strong>{abbreviationToDelete?.shortcode}</strong>? This action
              cannot be undone.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteAbbreviation.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
